
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter, Subject, takeUntil } from 'rxjs';
import { BreadcrumbItem, BreadcrumbMergeStrategy } from './breadcrumb.models';
import { BreadcrumbService } from './breadcrumb.service';

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterModule, MatIconModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  @Input() manualItems: BreadcrumbItem[] = [];
  @Input() mergeStrategy: BreadcrumbMergeStrategy = 'auto';

  items: BreadcrumbItem[] = [];

  private readonly destroy$ = new Subject<void>();
  private serviceManualItems: BreadcrumbItem[] = [];
  private serviceStrategy: BreadcrumbMergeStrategy = 'auto';

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    // Listen to service changes
    this.breadcrumbService
      .getManualItems$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.serviceManualItems = items ?? [];
        this.rebuild(this.serviceManualItems, this.serviceStrategy);
      });

    this.breadcrumbService
      .getStrategy$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(strategy => {
        this.serviceStrategy = strategy;
        this.rebuild(this.serviceManualItems, this.serviceStrategy);
      });

    // Rebuild on navigation changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(() => this.rebuild(this.serviceManualItems, this.serviceStrategy));

    // Initial build
    this.rebuild(this.serviceManualItems, this.serviceStrategy);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private rebuild(serviceManualItems?: BreadcrumbItem[], serviceStrategy?: BreadcrumbMergeStrategy): void {
    const autoItems = this.buildFromRoute(this.activatedRoute.root.snapshot);
    const manual = this.manualItems?.length ? this.manualItems : (serviceManualItems ?? []);
    const strategy = this.mergeStrategy || serviceStrategy || 'auto';
    this.items = this.merge(autoItems, manual, strategy);
  }

  private merge(autoItems: BreadcrumbItem[], manualItems: BreadcrumbItem[], strategy: BreadcrumbMergeStrategy): BreadcrumbItem[] {
    switch (strategy) {
      case 'append':
        return [...autoItems, ...manualItems];
      case 'prepend':
        return [...manualItems, ...autoItems];
      case 'replace':
        return [...manualItems];
      case 'auto':
      default:
        return [...autoItems];
    }
  }

  private buildFromRoute(root: ActivatedRouteSnapshot): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];
    let url = '';
    let route: ActivatedRouteSnapshot | null = root;

    while (route) {
      const path = route.url.map(segment => segment.path).filter(Boolean).join('/');
      if (path) {
        url += `/${path}`;
      }

      const label = route.data?.['breadcrumb'] || undefined;

      if (label) {
        breadcrumbs.push({ label, url });
      }

      route = (route.firstChild as ActivatedRouteSnapshot) || null;
    }

    return breadcrumbs;
  }
}


